import {
  ACCOUNT_CATEGORY,
  CHAIN_NAMESPACES,
  type ChainNamespaceType,
  PROVIDER_NOTIFICATIONS,
  ProviderConfig,
  type THEME,
  UserInfo,
} from "@toruslabs/base-controllers";
import {
  type AccountAbstractionMultiChainConfig,
  type EthereumGasState,
  EthereumTransactionMeta,
  MAINNET_CHAIN_ID,
  SMART_ACCOUNT_EIP_STANDARD,
} from "@toruslabs/ethereum-controllers";
import { remove0x } from "@toruslabs/metadata-helpers";
import { CHAIN_IDS, SolanaTransactionMeta } from "@toruslabs/solana-controllers";
import {
  AUTH_CONNECTION_TYPE,
  PostMessageStream,
  SafeEventEmitterProvider,
  setupMultiplex,
  Substream,
  type THEME_MODE_TYPE,
  THEME_MODES,
  type WHITE_LABEL_THEME,
} from "@web3auth/auth";
import { type ProviderConfigOption } from "@web3auth/portfolio";
import { cloneDeep, merge } from "lodash-es";
import log from "loglevel";
import { defineStore, StateTree } from "pinia";
import { computed, Ref, ref, shallowRef, watch } from "vue";

import useUtils from "../composables/use-utils";
import config from "../config";
import ControllerFactory, { DEFAULT_CONFIG, DEFAULT_STATE } from "../controllers/ControllerFactory";
import type EthereumController from "../controllers/EthereumController";
import { EthereumControllerState, ITorusController, ITorusControllerState } from "../controllers/interfaces";
import type SolanaController from "../controllers/SolanaController";
import { loadLanguageAsync } from "../plugins/i18n-Setup";
import { formatActivityTransactions } from "../utils/activityTransactions";
import { analytics } from "../utils/analytics";
import { TORUS_CONTROLLER_EVENTS } from "../utils/enums";
import { APP_PLATFORMS_TYPE, BaseFormattedTransactionActivity, LOGIN_MODE, TOAST_TYPES } from "../utils/interfaces";
import { checkSolanaChainNameSpace, getCaipChainId, getHostname, getUserLanguage, isMain, MULTI_CHAIN_SUPPORTED_NETWORKS } from "../utils/utils";
import { useToastStore } from "./toastStore";

const {
  getUserThemeFromStorage,
  getUserLocaleFromStorage,
  getUserCurrencyFromStorage,
  setUserLocaleToStorage,
  setUserCurrencyToStorage,
  setTheme,
  dashboardApplicationMap,
} = useUtils();

const localUserTheme = await getUserThemeFromStorage();
const localUserLocale = await getUserLocaleFromStorage();
const localUserCurrency = await getUserCurrencyFromStorage();

const defaultEvmChainId = config.defaultChainIds[CHAIN_NAMESPACES.EIP155];
const builtInChainsAll = Object.values(MULTI_CHAIN_SUPPORTED_NETWORKS) as ProviderConfigOption[];
const fallbackById = builtInChainsAll.find((c) => c.chainId === defaultEvmChainId);
const fallbackEvmChain = fallbackById || builtInChainsAll.find((c) => c.chainNamespace === CHAIN_NAMESPACES.EIP155);

export const useControllerStore = defineStore(
  `controller-${config.embedNonce}`,
  () => {
    const controllerFactory = new ControllerFactory();
    const torusCtrl = shallowRef<ITorusController>(null);
    const chainNamespace = ref<ChainNamespaceType>(config.defaultChainNamespace);
    const aggregateChainState = ref<Record<string, ITorusControllerState>>(cloneDeep(DEFAULT_STATE));

    const accountAbstractionConfig: Ref<AccountAbstractionMultiChainConfig | null> = ref(null);
    const chainConfigs: Ref<ProviderConfig[]> = ref([]);

    const sessionId = ref("");
    const showTestNetworks = ref(false);
    const provider: Ref<SafeEventEmitterProvider> = ref(null);
    const storageUserTheme = ref<THEME_MODE_TYPE>(localUserTheme);
    const storageUserLocale = ref<string | null>(localUserLocale);
    const toastStore = useToastStore();
    const platform = ref<APP_PLATFORMS_TYPE>(null); // used to determine if wallet is used by an external app (mobile, extension, etc.)
    const { loginMode, setLoginMode } = useUtils();
    const isUserSkippedMfa = ref(false);

    const torusState = computed(() => {
      return aggregateChainState.value[chainNamespace.value];
    });

    const whiteLabelTheme = computed((): WHITE_LABEL_THEME => {
      if ((!isMain || platform.value) && torusState.value.EmbedControllerState.whiteLabel?.theme)
        return torusState.value.EmbedControllerState.whiteLabel.theme;
      return {};
    });

    const userTheme = computed((): THEME_MODE_TYPE => {
      if ((!isMain || platform.value) && torusState.value.EmbedControllerState.whiteLabel?.mode)
        return torusState.value.EmbedControllerState.whiteLabel.mode;
      return storageUserTheme.value as THEME_MODE_TYPE;
    });

    const userLocale = computed((): string => {
      if ((!isMain || platform.value) && torusState.value.EmbedControllerState.whiteLabel?.defaultLanguage)
        return torusState.value.EmbedControllerState.whiteLabel.defaultLanguage;
      return storageUserLocale.value ?? getUserLanguage();
    });

    const userInfo = computed(() => {
      const { selectedAddress: address, identities } = torusState.value.PreferencesControllerState;
      return identities[address]?.userInfo || ({} as UserInfo);
    });

    const isDark = computed(() => {
      if (userTheme.value === THEME_MODES.dark) return true;
      if (userTheme.value === THEME_MODES.light) return false;
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    });

    const networkProviderConfig = computed((): ProviderConfigOption => {
      return {
        ...torusState.value.NetworkControllerState.providerConfig,
        id: parseInt(torusState.value.NetworkControllerState.providerConfig.chainId, 16),
      };
    });

    const selectedAddress = computed(() => {
      return torusState.value.PreferencesControllerState.selectedAddress;
    });

    const selectedNetworkTransactionsContext = computed(() => {
      const currentState = torusState.value;
      const address = selectedAddress.value;

      return {
        currentChainNamespace: chainNamespace.value,
        address,
        smartAccountAddress:
          chainNamespace.value === CHAIN_NAMESPACES.EIP155
            ? (currentState as EthereumControllerState).AccountAbstractionControllerState?.smartAccountAddress || ""
            : "",
        blockExplorerUrl: currentState.NetworkControllerState.providerConfig.blockExplorerUrl,
        currentCurrency: currentState.CurrencyControllerState.currentCurrency,
        conversionRate: currentState.CurrencyControllerState.conversionRate,
        nativeTicker: currentState.NetworkControllerState.providerConfig.ticker,
        rpcTarget: currentState.NetworkControllerState.providerConfig.rpcTarget,
        transactions: currentState.TransactionControllerState.transactions,
        tokenRates: currentState.TokenRatesControllerState.contractExchangeRates,
        tokens: currentState.TokensControllerState.tokens[address] ?? [],
        nfts: currentState.NftsControllerState.nfts[address] ?? [],
      };
    });

    const selectedNetworkTransactions = ref<BaseFormattedTransactionActivity[]>([]);
    let selectedNetworkTransactionsRequestId = 0;
    // show local transactions from transaction controller for selected network
    watch(
      selectedNetworkTransactionsContext,
      async (context) => {
        const requestId = ++selectedNetworkTransactionsRequestId;

        try {
          const rawMetas = Object.values(context.transactions || {});
          const transactionMetas =
            context.currentChainNamespace === CHAIN_NAMESPACES.EIP155
              ? (rawMetas as EthereumTransactionMeta[])
              : (rawMetas as SolanaTransactionMeta[]);

          const formattedTransactions = await formatActivityTransactions({
            chainNamespace: context.currentChainNamespace,
            selectedAddress: context.address,
            smartAccountAddress: context.smartAccountAddress,
            blockExplorerUrl: context.blockExplorerUrl,
            currentCurrency: context.currentCurrency,
            conversionRate: context.conversionRate,
            nativeTicker: context.nativeTicker,
            rpcTarget: context.rpcTarget,
            transactionMetas,
            tokenRates: context.tokenRates,
            tokens: context.tokens,
            nfts: context.nfts,
          });

          if (requestId === selectedNetworkTransactionsRequestId) {
            selectedNetworkTransactions.value = formattedTransactions;
          }
        } catch (error) {
          log.error("Error formatting activity transactions", error);
          if (requestId === selectedNetworkTransactionsRequestId) {
            selectedNetworkTransactions.value = [];
          }
        }
      },
      { immediate: true }
    );

    const chainOptions = computed((): ProviderConfigOption[] => {
      const allChains = [...builtInChainsAll];

      // only display chains that are defined by the embed
      // const embedChains = chainConfigs.value.map((config) => ({
      //   ...config,
      //   id: parseInt(config.chainId, 16),
      // }));
      const embedChains = chainConfigs.value as ProviderConfigOption[];
      if (embedChains?.length > 0) {
        const chainMap = allChains.reduce(
          (acc, chain) => {
            acc[chain.chainId] = chain;
            return acc;
          },
          {} as Record<string, ProviderConfigOption>
        );
        const finalEmbedChains = embedChains.map((chain) => {
          const existingChain = chainMap[chain.chainId];
          if (existingChain && existingChain.displayName === chain.displayName) return existingChain;
          return { ...chain, isCustom: true, isTestnet: existingChain?.isTestnet ?? false };
        });
        let result = finalEmbedChains;
        if (isUserSkippedMfa.value) {
          result = result.filter((c) => c.chainNamespace !== CHAIN_NAMESPACES.SOLANA);
        }
        // fallback to default evm if no chains config are available
        if (result.length === 0) {
          if (fallbackEvmChain) result = [fallbackEvmChain];
        }
        return result;
      }

      let filteredAllChains = isUserSkippedMfa.value ? allChains.filter((c) => c.chainNamespace !== CHAIN_NAMESPACES.SOLANA) : allChains;
      // fallback to default evm if no chains config are available
      if (filteredAllChains.length === 0) {
        if (fallbackEvmChain) filteredAllChains = [fallbackEvmChain];
      }
      return filteredAllChains;
    });

    // Ensure we are never on Solana if MFA is skipped
    async function ensureAllowedChain(): Promise<void> {
      if (isUserSkippedMfa.value && chainNamespace.value === CHAIN_NAMESPACES.SOLANA) {
        // fallback to available evm if no chains config are available
        const targetEvm = fallbackEvmChain || chainOptions.value.find((x) => x.chainNamespace === CHAIN_NAMESPACES.EIP155);
        if (targetEvm) {
          await setNetwork(targetEvm.displayName);
        }
      }
    }

    function updateTorusState(state: ITorusControllerState): void {
      aggregateChainState.value = { ...aggregateChainState.value, [chainNamespace.value]: state };
    }

    function logout(): void {
      // logout from all controllers
      [CHAIN_NAMESPACES.EIP155, CHAIN_NAMESPACES.SOLANA].forEach((namespace) => {
        const controller = controllerFactory.getController(namespace);
        // skip if controller is not initialized
        if (!controller) return;

        const networkState = controller.state.NetworkControllerState || DEFAULT_STATE[namespace].NetworkControllerState;
        const embedState = isMain ? DEFAULT_STATE[namespace].EmbedControllerState : controller.state.EmbedControllerState;
        const initialState = {
          ...cloneDeep(DEFAULT_STATE[namespace]),
          NetworkControllerState: cloneDeep(networkState),
          EmbedControllerState: cloneDeep(embedState),
        };
        controller.setControllersState(initialState);
        controller.setOrigin(controller.origin);
      });
      setIsUserSkippedMfa(false);
    }

    async function importAccount(privKey: string): Promise<string> {
      const address = await torusCtrl.value?.addAccount({
        privKey: privKey.padStart(64, "0"),
        userInfo: torusCtrl.value?.userInfo,
        type: ACCOUNT_CATEGORY.IMPORTED,
        loginMode: loginMode.value,
      });
      torusCtrl.value?.setSelectedAccount(address);
      return address;
    }

    async function setSelectedAccount(address: string) {
      torusCtrl.value?.setSelectedAccount(address);
    }

    async function setUserTheme(theme: THEME_MODE_TYPE) {
      await setTheme(theme);
      storageUserTheme.value = theme;
    }

    async function setUserLocale(locale: string) {
      storageUserLocale.value = locale;
      await setUserLocaleToStorage(locale);
      await loadLanguageAsync(locale);
    }

    function setUserCurrency(currency: string) {
      torusCtrl.value?.setSelectedCurrency(currency);
      void setUserCurrencyToStorage(currency);
    }

    // eslint-disable-next-line prefer-const
    let handleNetworkChange: (providerConfig: ProviderConfigOption) => Promise<void>;
    async function init({ state, origin }: { state?: Partial<ITorusControllerState>; origin: string }): Promise<void> {
      // set chainNamespace from state
      const chaindIdParam = state?.NetworkControllerState?.chainId || torusState.value?.NetworkControllerState?.chainId || config.getDefaultChainId();
      const chainNamespaceParam = MULTI_CHAIN_SUPPORTED_NETWORKS[chaindIdParam]?.chainNamespace || config.defaultChainNamespace;
      chainNamespace.value = chainNamespaceParam;

      // set analytics traits and global properties
      const { web3AuthClientId, web3AuthNetwork, dappMetadata } = state?.EmbedControllerState || {};
      analytics.identify(web3AuthClientId || config.web3AuthClientId, {
        web3auth_client_id: web3AuthClientId || config.web3AuthClientId,
        web3auth_network: web3AuthNetwork || config.web3AuthNetwork,
      });
      analytics.setGlobalProperties({
        dapp_name: dappMetadata?.name,
        chain_namespace: chainNamespaceParam,
        chain_id: getCaipChainId({ chainNamespace: chainNamespaceParam, chainId: chaindIdParam }),
      });

      // init controller
      let controller = controllerFactory.getController(chainNamespace.value);
      const is7702SmartAccount = accountAbstractionConfig.value?.smartAccountEipStandard === SMART_ACCOUNT_EIP_STANDARD.EIP_7702;
      if (controller) {
        const currentChainId = torusState.value.NetworkControllerState.chainId;
        if (state.NetworkControllerState.providerConfig) controller.setNetwork(state.NetworkControllerState.providerConfig);
        if (currentChainId !== chaindIdParam) {
          // setup account abstraction controller if chain changes (skip for EIP-7702)
          if (accountAbstractionConfig.value && chainNamespace.value === CHAIN_NAMESPACES.EIP155 && !is7702SmartAccount) {
            (controller as EthereumController).setupAccountAbstractionController(chaindIdParam);
          }
        }
      } else {
        controller = await controllerFactory.init({
          chainNamespace: chainNamespace.value,
          config: DEFAULT_CONFIG[chainNamespace.value],
          state: merge(torusState.value, state),
          switchChain: (chainId: string) => {
            const providerConfig = chainOptions.value.find((x) => x.chainId === chainId);
            if (!providerConfig) throw new Error(`Unsupported network: ${chainId}`);
            // execute this async as when changing namespace, the current provider communication will be dropped and cannot give a response
            handleNetworkChange(providerConfig);
          },
          getAccountAbstractionMultiChainConfig: () => accountAbstractionConfig.value,
        });
        // when switching namespace, controller will be initialized and we want to preserve the existing login mode if it's not the default value "main"
        if (loginMode.value === LOGIN_MODE.main) setLoginMode(isMain ? LOGIN_MODE.main : LOGIN_MODE.embed);
        // setup account abstraction controller (skip for EIP-7702)
        if (accountAbstractionConfig.value && chainNamespace.value === CHAIN_NAMESPACES.EIP155 && !is7702SmartAccount) {
          const bundlerHostnames = Array.from(
            new Set(accountAbstractionConfig.value?.chains?.map((chain) => getHostname(chain.bundlerConfig?.url)).filter(Boolean))
          );
          const paymasterHostnames = Array.from(
            new Set(accountAbstractionConfig.value?.chains?.map((chain) => getHostname(chain.paymasterConfig?.url)).filter(Boolean))
          );
          (controller as EthereumController).setupAccountAbstractionController(chaindIdParam);
          analytics.setGlobalProperties({
            aa_smart_account_type: accountAbstractionConfig.value?.smartAccountType,
            aa_chain_ids: accountAbstractionConfig.value?.chains.map((chain) =>
              getCaipChainId({ chainId: chain.chainId, chainNamespace: CHAIN_NAMESPACES.EIP155 })
            ),
            aa_bundler_urls: bundlerHostnames,
            aa_paymaster_urls: paymasterHostnames,
            aa_paymaster_enabled: paymasterHostnames.length > 0,
            aa_paymaster_context_enabled: accountAbstractionConfig.value?.chains?.some((chain) => chain.bundlerConfig?.paymasterContext),
            aa_erc20_paymaster_enabled: accountAbstractionConfig.value?.chains?.some(
              (chain) => (chain.bundlerConfig?.paymasterContext as { token: string })?.token
            ),
          });
        }
      }

      torusCtrl.value = controller;

      if (isMain && localUserCurrency) {
        controller.setSelectedCurrency(localUserCurrency);
      }

      controller.setOrigin(origin);
      controller.on(TORUS_CONTROLLER_EVENTS.STORE, (store: ITorusControllerState) => {
        updateTorusState(store);
      });

      provider.value = controller.provider;

      controller.on(TORUS_CONTROLLER_EVENTS.LOGIN_RESPONSE, (message: string, _, loginSessionId: string) => {
        if (message) toastStore.setToastMessage({ message, type: TOAST_TYPES.ERROR });
        if (loginSessionId) sessionId.value = loginSessionId;
      });
    }

    async function triggerLogin({
      authConnection,
      login_hint: loginHint,
      theme,
    }: {
      authConnection: AUTH_CONNECTION_TYPE;
      login_hint?: string;
      theme: THEME;
    }): Promise<void> {
      const result = await torusCtrl.value?.triggerLogin({ authConnection, login_hint: loginHint, theme });
      setIsUserSkippedMfa(result.isUserSkippedMfa);
      await ensureAllowedChain();

      loadLanguageAsync(userLocale.value);
      await setTheme(userTheme.value);
    }

    let providerSubStream: Substream;
    let communicationSubStream: Substream;
    let cancelCommunication: () => void = () => {};
    function setupCommunication(origin: string): void {
      log.info("setting up communication with", origin);
      if (!providerSubStream) {
        const torusStream = new PostMessageStream({
          name: "iframe_torus",
          target: "embed_torus",
          targetWindow: window.parent,
          targetOrigin: origin,
        });
        // connect features && for test cases
        const torusMux = setupMultiplex(torusStream);
        // We create the mux so that we can handle phishing stream here
        providerSubStream = torusMux.createStream("provider");
      }

      if (!communicationSubStream) {
        const communicationStream = new PostMessageStream({
          name: "iframe_communication",
          target: "embed_communication",
          targetWindow: window.parent,
          targetOrigin: origin,
        });
        // connect features && for test cases
        const torusMux = setupMultiplex(communicationStream);
        // We create the mux so that we can handle phishing stream here
        communicationSubStream = torusMux.createStream("provider");
      }

      cancelCommunication = torusCtrl.value?.setupCommunicationChannels(providerSubStream, communicationSubStream, origin);
    }

    handleNetworkChange = async (providerConfigOption: ProviderConfigOption): Promise<void> => {
      const currentChainId = torusState.value.NetworkControllerState.chainId;
      const incomingChainId = providerConfigOption.chainId;
      if (currentChainId === incomingChainId) return;

      const currentChainNamespace = chainNamespace.value;
      const incomingChainNamespace = providerConfigOption.chainNamespace;

      // update global properties for analytics
      analytics.setGlobalProperties({
        chain_namespace: incomingChainNamespace,
        chain_id: getCaipChainId({ chainNamespace: incomingChainNamespace, chainId: incomingChainId }),
      });

      // clone the provider config object from the provider config option to suppress the ts-lint error
      const providerConfig = { ...providerConfigOption };

      // when switch chains in the same namespace, just set the network
      if (currentChainNamespace === incomingChainNamespace) {
        torusCtrl.value?.setNetwork(providerConfig);
        // setup account abstraction controller (skip for EIP-7702)
        const is7702SmartAccount = accountAbstractionConfig.value?.smartAccountEipStandard === SMART_ACCOUNT_EIP_STANDARD.EIP_7702;
        if (accountAbstractionConfig.value && incomingChainNamespace === CHAIN_NAMESPACES.EIP155 && !is7702SmartAccount) {
          (torusCtrl.value as EthereumController).setupAccountAbstractionController(incomingChainId);
        }
      } else {
        // when switching to a different namespace
        // remove events, polling of old torus controller
        const oldController = torusCtrl.value;
        const { origin } = oldController;
        const logoutHandlers = oldController.listeners(TORUS_CONTROLLER_EVENTS.LOGOUT);
        const confirmationModalShowHandlers = oldController.listeners(TORUS_CONTROLLER_EVENTS.CONFIRMATION_MODAL_SHOW);
        oldController.removeAllListeners();
        oldController.stopPolling();
        if (!isMain) cancelCommunication();

        // init controller
        const embedState = torusState.value.EmbedControllerState;
        const updatedEmbedState = { ...embedState, chainConfig: providerConfig };
        chainNamespace.value = providerConfigOption.chainNamespace;
        await init({
          state: {
            EmbedControllerState: { ...updatedEmbedState },
            NetworkControllerState: {
              chainId: providerConfigOption.chainId,
              providerConfig,
              properties: {},
            },
          },
          origin,
        });

        // re-add logout handlers to new controller
        logoutHandlers?.forEach((handler) => torusCtrl.value?.on(TORUS_CONTROLLER_EVENTS.LOGOUT, handler));
        confirmationModalShowHandlers?.forEach((handler) => torusCtrl.value?.on(TORUS_CONTROLLER_EVENTS.CONFIRMATION_MODAL_SHOW, handler));
        if (!isMain) {
          setupCommunication(origin);
          // when switch to a different chain namespaces, lookupNetwork can take longer leading to account changed being notified before chain changed
          // this can cause issues that the provider is still using the old chain namespace
          // so wait util the chain changed event is emitted before notifying account changed
          await new Promise((resolve) => {
            torusCtrl.value?.providerNotificationEmitter?.once("notification", (event) => {
              const { method } = event as { method: string };
              if (method === PROVIDER_NOTIFICATIONS.CHAIN_CHANGED) {
                resolve(true);
              }
            });
          });
        }

        const result = await torusCtrl.value?.rehydrate();
        setIsUserSkippedMfa(result.isUserSkippedMfa);
      }
    };

    function isChainIdSupported(chainId: string): boolean {
      return chainOptions.value.some((x) => remove0x(x.chainId) === remove0x(chainId));
    }

    async function setNetwork(displayName: string): Promise<void> {
      const providerConfig = chainOptions.value.find((x) => x.displayName === displayName);
      if (!providerConfig) throw new Error(`Unsupported network: ${displayName}`);
      return handleNetworkChange(providerConfig);
    }

    function setShowTestNetworks(value: boolean) {
      showTestNetworks.value = value;
    }

    function setPlatform(value: APP_PLATFORMS_TYPE) {
      analytics.setGlobalProperties({ app_platform: value });
      platform.value = value;
    }

    function setIsUserSkippedMfa(value: boolean) {
      isUserSkippedMfa.value = value;
    }

    function setAccountAbstractionConfig(aaConfig: AccountAbstractionMultiChainConfig) {
      accountAbstractionConfig.value = aaConfig;
    }

    function setChainConfigs(chainsParams: ProviderConfig[]) {
      chainConfigs.value = chainsParams;
      // If chain configs change, verify allowed chain
      ensureAllowedChain();
    }

    function getUmi() {
      if (checkSolanaChainNameSpace(chainNamespace.value)) {
        return (torusCtrl.value as SolanaController)?.umi;
      }
      return null;
    }

    const gasFees = computed((): EthereumGasState => {
      if (chainNamespace.value === CHAIN_NAMESPACES.EIP155) {
        return (torusState.value as EthereumControllerState).GasFeeControllerState;
      }
      return null;
    });

    function getGasPriceParams(customOptions?: {
      advancedOptionData?: { maxFeePerGas?: string; maxPriorityFeePerGas?: string; gasPrice?: string };
      isAdvancedOption?: boolean;
      selectedSpeedValue?: string;
    }) {
      if (checkSolanaChainNameSpace(chainNamespace.value)) {
        return {};
      }
      return (torusCtrl.value as EthereumController).getGasPriceParams(customOptions);
    }

    async function setSelectedAccountByDappId(dappData: { dappId: string; dappChainNamespace: string }) {
      let targetDappAccount = dashboardApplicationMap.value[dappData.dappId];
      const currentChainId = torusState.value.NetworkControllerState.providerConfig.chainId;
      let targetChainId = MAINNET_CHAIN_ID;
      if (targetDappAccount) {
        if (dappData.dappChainNamespace === CHAIN_NAMESPACES.SOLANA) targetChainId = CHAIN_IDS.SOLANA_MAINNET;

        const targetChain = chainOptions.value.find((chain) => chain.chainId === targetChainId);
        if (targetChainId !== currentChainId && targetChain) {
          await setNetwork(targetChain.displayName);
        }
        targetDappAccount = dashboardApplicationMap.value[dappData.dappId as string];
        torusCtrl.value?.setSelectedAccount(targetDappAccount);
      }
    }

    return {
      aggregateChainState,
      chainNamespace,
      init,
      triggerLogin,
      torusCtrl,
      logout,
      torusState,
      setupCommunication,
      importAccount,
      setSelectedAccount,
      networkProviderConfig,
      setNetwork,
      isChainIdSupported,
      provider,
      selectedNetworkTransactions,
      gasFees,
      selectedAddress,
      chainOptions,
      getUmi,
      showTestNetworks,
      setShowTestNetworks,
      setUserTheme,
      setUserLocale,
      setUserCurrency,
      userTheme,
      whiteLabelTheme,
      isDark,
      userLocale,
      userInfo,
      platform,
      isUserSkippedMfa,
      setIsUserSkippedMfa,
      ensureAllowedChain,
      setPlatform,
      setAccountAbstractionConfig,
      setChainConfigs,
      getGasPriceParams,
      setSelectedAccountByDappId,
    };
  },
  {
    persist: [
      {
        serializer: {
          serialize: (value: StateTree) => {
            const clonedValue = cloneDeep(value);
            Object.keys(clonedValue.aggregateChainState as Record<string, ITorusControllerState>).forEach((chainNamespace) => {
              delete clonedValue.aggregateChainState[chainNamespace].KeyringControllerState;
              delete clonedValue.aggregateChainState[chainNamespace].PreferencesControllerState;
            });

            delete clonedValue.provider;
            delete clonedValue.torusCtrl;
            return JSON.stringify(clonedValue);
          },
          deserialize: (value: string) => {
            return JSON.parse(value);
          },
        },
        storage: window.sessionStorage,
      },
    ],
  }
);
